import {
  EmptyState,
  FlexColumn,
  FlexRow,
  LabeledInput,
  Loading,
  MaterialIcon,
  StyledButton,
  useLoading,
  useStyles,
} from '@gorila-shared-ui/components';
import { LabelSmall } from 'baseui/typography';
import { useRef, useState } from 'react';
import AutoSizer from 'react-virtualized-auto-sizer';
import { VariableSizeList as List } from 'react-window';
import InfiniteLoader from 'react-window-infinite-loader';
import { DEFAULT_LIST_ITEM_HEIGHT } from '../../constants/app';
import { useDebounceFunction } from '../../hooks/useDebounce';
import useUpdateEffect from '../../hooks/useUpdateEffect';
import { getAssetsDataSearch } from '../../services/assetService';
import { Asset } from '../../types/asset';
import SearchAssetItem from './assetListPicker/SearchAssetItem';

type Props = {
  onSelectedAssetId?: (assetId?: string) => void;
  onSelectedAsset?: (asset?: Asset) => void;
  label?: string;
  helpText?: React.ReactNode;
  onCancel?: () => void;
};
export function SearchAssetData({
  onSelectedAsset,
  onSelectedAssetId,
  label = 'Activo:',
  helpText = 'Buscar activo por etiqueta, placa, ident o número de serie(VIN)',
  onCancel,
}: Readonly<Props>) {
  const { css, theme } = useStyles();
  const [assetsList, setAssetsList] = useState<Asset[]>();
  const { loading, startLoading, stopLoading } = useLoading();
  const [page, setPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [search, setSearch] = useState<string>();
  const [hasSearched, setHasSearched] = useState(false);
  const requestIdRef = useRef(0);
  const isGlobalLoading = loading && page <= 1;
  const isAssetsLoaded = (index: number) => !hasNextPage || index < (assetsList?.length ?? 0);
  const assetCount = hasNextPage ? (assetsList?.length ?? 0) + 1 : assetsList?.length ?? 0;

  useUpdateEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    startLoading();
    const loadAssets = async () => {
      const requestId = ++requestIdRef.current;
      const { assets, error, hasNext } = await getAssetsDataSearch(page, search);
      if (requestId === requestIdRef.current) {
        if (!error && assets) {
          if (page > 1 && assetsList) {
            setAssetsList([...assetsList, ...assets]);
            setHasNextPage(hasNext);
          } else {
            setAssetsList(assets);
            setHasNextPage(hasNext);
          }
        } else {
          setAssetsList(undefined);
          setHasNextPage(false);
        }
        stopLoading();
      }
    };
    loadAssets();
  }, [page]);

  const fetchData = useDebounceFunction(function () {
    setPage((prev) => prev + 1);
  }, 500);

  const handleSearch = () => {
    setPage(0);
    setHasSearched(true);
  };

  const onSelectAsset = (asset: Asset) => {
    onSelectedAsset && onSelectedAsset(asset);
    onSelectedAssetId && onSelectedAssetId(asset._id);
  };

  return (
    <FlexColumn>
      {!hasSearched && (
        <FlexRow gap={theme.sizing.scale300}>
          <LabeledInput
            label={label}
            value={search}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSearch();
              }
            }}
            onChange={setSearch}
            placeholder="Buscar activo..."
            caption={helpText}
          />
          <span className={css({ marginTop: '22px' })}>
            <StyledButton
              type="submit"
              kind="tertiary"
              size="compact"
              onClick={handleSearch}
            >
              <FlexRow gap={theme.sizing.scale200}>
                <LabelSmall>Buscar</LabelSmall>
                <MaterialIcon
                  name="search"
                  size="16px"
                />
              </FlexRow>
            </StyledButton>
          </span>
        </FlexRow>
      )}
      {hasSearched && isGlobalLoading && (
        <FlexColumn
          centered
          classNames={css({
            minHeight: '200px',
          })}
        >
          <Loading />
        </FlexColumn>
      )}
      {hasSearched && !isGlobalLoading && (
        <>
          {!!assetsList?.length && (
            <FlexColumn>
              <ul
                className={css({
                  margin: 0,
                  paddingLeft: 0,
                  paddingRight: 0,
                  height: '50vh',
                })}
              >
                <AutoSizer>
                  {({ width, height }) => (
                    <InfiniteLoader
                      isItemLoaded={isAssetsLoaded}
                      itemCount={assetCount}
                      loadMoreItems={fetchData}
                    >
                      {({ onItemsRendered, ref }) => (
                        <List
                          height={height ? height : 500}
                          itemCount={assetCount}
                          itemSize={() => DEFAULT_LIST_ITEM_HEIGHT}
                          width={width ? width : 200}
                          itemData={{
                            assets: assetsList,
                            onClick: (asset) => onSelectAsset(asset as Asset),
                          }}
                          onItemsRendered={onItemsRendered}
                          overscanCount={4}
                          ref={ref}
                        >
                          {SearchAssetItem}
                        </List>
                      )}
                    </InfiniteLoader>
                  )}
                </AutoSizer>
              </ul>
              <StyledButton
                kind="tertiary"
                size="compact"
                onClick={() => {
                  setHasSearched(false);
                  setSearch('');
                  setAssetsList(undefined);
                }}
              >
                Nueva busqueda
              </StyledButton>
            </FlexColumn>
          )}
          {!assetsList?.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
              style={{
                padding: '48px 0',
              }}
              action={
                <FlexRow gap={theme.sizing.scale300}>
                  {!!onCancel && (
                    <StyledButton
                      kind="secondary"
                      size="compact"
                      onClick={onCancel}
                    >
                      Llenar manualmente
                    </StyledButton>
                  )}
                  <StyledButton
                    kind="primary"
                    size="compact"
                    onClick={() => {
                      setHasSearched(false);
                      setSearch('');
                      setAssetsList(undefined);
                    }}
                  >
                    Continuar
                  </StyledButton>
                </FlexRow>
              }
              centered={true}
            />
          )}
        </>
      )}
    </FlexColumn>
  );
}
